import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { HcaId, HcaLibraryResponse, HcaSectionId, HcaSectionResponse, HcaStepId, HcaStepResponse } from '@business/entities/hca_library';
import { HcaLibraryAPI } from '@business/api/hca_library';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { HcaSectionAPI } from '@business/api/hca_section';
import { HcaStepAPI } from '@business/api/hca_step';

export class HcaLibraryBloc extends BaseBloc {
  private readonly hcaLibraryRepo = new Repository<HcaLibraryResponse>({
    getItemId: hcaLibrary => hcaLibrary.id,
  });

  readonly hcaLibrary$ = this.hcaLibraryRepo.items$;
  readonly hcaVolume = this.hcaLibraryRepo.selectItem;

  onReset() {
    this.hcaLibraryRepo.reset();
  }

  fetchHcaLibraries = () => {
    return HcaLibraryAPI.fetchHcaLibraries().pipe(
      this.hcaLibraryRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_hca_library_error') }),
    );
  };

  fetchHcaSteps = () => {
    return HcaLibraryAPI.fetchHcaSteps().pipe(handleMessage({ error: requestMessage('fetch_hca_library_error') }));
  };

  fetchHcaLibrary = (id: HcaId) => {
    return HcaLibraryAPI.fetchHcaLibrary(id).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_hca_library_error') }),
    );
  };

  createVolume = (volume: Partial<HcaLibraryResponse>) => {
    return HcaLibraryAPI.createVolume(volume).pipe(
      this.hcaLibraryRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_hca_volume'),
      }),
    );
  };

  updateVolume = (id: HcaId, brand: Partial<HcaLibraryResponse>) => {
    return HcaLibraryAPI.updateVolume(id, brand).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_hca_volume'),
      }),
    );
  };

  orderLessons = (id: HcaId, brand: Partial<HcaLibraryResponse>) => {
    return HcaLibraryAPI.orderLessons(id, brand).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_hca_volume'),
      }),
    );
  };

  searchArticles = (search: string[]) => {
    return HcaLibraryAPI.searchArticles(search);
  };

  deleteVolume = (id: HcaId) => {
    return showDeleteConfirmation('Delete Volume', 'Do you really want to remove this Volume?').pipe(
      switchMap(() => HcaLibraryAPI.deleteVolume(id)),
      this.hcaLibraryRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_hca_volume'),
      }),
    );
  };

  fetchParentSections = (id: HcaSectionId) => {
    return HcaLibraryAPI.fetchParentSections(id).pipe(handleMessage({ error: requestMessage('fetch_hca_library_error') }));
  };

  // Sections APIs
  createSection = (volume: Partial<HcaSectionResponse>) => {
    return HcaSectionAPI.createSection(volume).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_hca_volume'),
      }),
    );
  };

  updateSection = (id: HcaSectionId, brand: Partial<HcaSectionResponse>) => {
    return HcaSectionAPI.updateSection(id, brand).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_hca_volume'),
      }),
    );
  };

  deleteSection = (id: HcaSectionId) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => HcaSectionAPI.deleteSection(id)),
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_hca_volume'),
      }),
    );
  };

  // Steps APIs
  createStep = (volume: Partial<HcaStepResponse>) => {
    return HcaStepAPI.createStep(volume).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_hca_volume'),
      }),
    );
  };

  updateStep = (id: HcaStepId, brand: Partial<HcaStepResponse>) => {
    return HcaStepAPI.updateStep(id, brand).pipe(
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_hca_volume'),
      }),
    );
  };

  deleteStep = (id: HcaStepId) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => HcaStepAPI.deleteStep(id)),
      this.hcaLibraryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_hca_volume'),
      }),
    );
  };
}
